import { bool } from "prop-types";
import { useMemo, useRef } from "react";

import styled from "styled-components";

import { useStartOver } from "Login/TwoFA/API/queryHooks";
import { useTwoFAContext } from "Login/TwoFA/TwoFA";
import { useIsLegacy } from "Login/TwoFA/stateHooks";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrTextButton from "UI/FnrTextButton";
import { marketingColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

const StartOver = ({ compact }) => {
  const buttonRef = useRef();
  const isHoverFocused = useIsHoverFocused(buttonRef);
  const { variant, isDark, accentColor, remoteCancelRef } = useTwoFAContext();
  const [{ isLoading }, startOver] = useStartOver({ variant, remoteCancelRef });
  const isLegacy = useIsLegacy();

  const color = useMemo(() => {
    if (isDark) return "textLight";
    if (isHoverFocused)
      return accentColor || (isLegacy ? "fnDarkBlue" : "brandFnBlue");
    return "textSecondary";
  }, [accentColor, isDark, isHoverFocused, isLegacy]);

  const backgroundColor = useMemo(() => {
    if (isDark && isHoverFocused && !isLoading)
      return addOpacity(marketingColors.grey, 0.2);
    return "#ffffff00";
  }, [isDark, isHoverFocused, isLoading]);

  if (compact)
    return (
      <SFnrButtonWrapper
        ref={buttonRef}
        onClick={startOver}
        data-cy="TwoFA_StartOver"
        aria-label="use-a-different-account"
      >
        <FnrTappyShrink growOnHover>
          <SFnrContainedIcon
            color={color}
            $backgroundColor={backgroundColor}
            isLoading={isLoading}
            icon="fa-chevron-left fas"
          />
        </FnrTappyShrink>
      </SFnrButtonWrapper>
    );
  return (
    <FnrTextButton
      isLoading={isLoading}
      onClick={startOver}
      icon="fa-user-slash fas"
      isLegacy={isLegacy}
      isDark={isDark}
      aria-label="use-a-different-account"
      emphasized
      data-cy="TwoFA_StartOver"
      subtleHover
    >
      Use a different account
    </FnrTextButton>
  );
};

const SFnrContainedIcon = styled(FnrContainedIcon)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  height: 28px;
  width: 40px;
`;

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 28px;
`;

StartOver.propTypes = { compact: bool };

export default StartOver;
